import { useLayoutEffect, useRef, useState } from 'react'
import { getCurrentBuild } from 'web/data/core-data'
import { CLASS_META } from 'web/data/general/id'
import { FightSetup, SpecRole } from 'web/data/models'
import { useOutsideClick } from 'web/hooks/useOutsideClick'
import { sortPlayers } from 'web/utils/sort-utils'
import { expectOneById, updateIndexCircular } from 'web/utils/utils'
import { GameIcon } from './GameIcon'
import classNames from 'classnames'

type SetupSelectBoxProps = {
    input: {
        bsSize: 'sm' | 'md' | 'lg' | 'xl'
    }
    select: {
        disabled: boolean
        setups: FightSetup[]
        onSelected: (option: FightSetup) => void

        selectedSetup?: string
    }
}

/**
 * The graphical implementation here is far too specialized to warrant
 * an abstraction.
 */
export function SetupSelectBox(props: SetupSelectBoxProps): JSX.Element | null {

    const buildData = getCurrentBuild()

    const [showDropdown, setShowDropdown] = useState(false)

    const containerRef = useRef<HTMLDivElement>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)

    const [selectedIndex, setSelectedIndex] = useState<number | null>(null)

    useOutsideClick({
        ref: containerRef,
        onOutsideClick: () => {
            setShowDropdown(false)
            setSelectedIndex(null)
        }
    })

    useLayoutEffect(() => {
        if (containerRef.current && dropdownRef.current) {
            dropdownRef.current.style.minWidth = `${containerRef.current.getBoundingClientRect().width}px`
        }
    })

    function renderSetupRow(setup: FightSetup, isSelected: boolean): JSX.Element {

        const healers = setup.fightPlayers.filter(player => {
            const role = buildData.specsById[player.specId].role
            return role === SpecRole.Healer
        })

        const specs = healers.sort(sortPlayers).map(healer => {
            return buildData.specsById[healer.specId]
        })

        return (
            <div
                key={setup.id}
                className={classNames(
                    "p-2 d-flex space-mx-1 justify-content-between",
                    { 'bg-primary text-white': isSelected },
                    { 'bg-light-hover': !isSelected }
                )}
                onClick={() => {
                    props.select.onSelected(setup)
                    setShowDropdown(false)
                    setSelectedIndex(null)
                }}
            >
                <div className="d-flex align-items-center px-1">
                    {/* <div title="Proven setup" className="mr-2 text-dark cursor-question">
                        <SvgPatch />
                    </div> */}

                    <div>
                        {setup.name}
                    </div>
                </div>
                {specs.length <= 0 ? (
                    <div
                        style={{ height: 28 }}
                        className="d-flex flex-shrink-0 bg-light text-dark rounded border space-mx-1 px-2 shadow-sm small align-items-center"
                    >
                        {'No healers added'}
                    </div>
                ) : (
                    <div className="d-flex flex-shrink-0 border bg-light rounded text-dark space-mx-1 px-1 shadow-sm">
                        {specs.map((spec, index) => {
                            return (
                                <div
                                    key={index}
                                    className="small d-flex align-items-center py-1"
                                >
                                    <GameIcon
                                        className={`border-color-${CLASS_META[spec.classId].shortName} border`}
                                        size="18px"
                                        icon_name={spec.icon_name}
                                    />
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>
        )
    }

    function renderSelectedSetup(setup: FightSetup): JSX.Element {

        const healers = setup.fightPlayers.filter(player => {
            const role = buildData.specsById[player.specId].role
            return role === SpecRole.Healer
        })

        const specs = healers.sort(sortPlayers).map(healer => {
            return buildData.specsById[healer.specId]
        })

        const moreThan5Healers = specs.length >= 6

        return (
            <div
                className="d-flex justify-content-between flex-wrap"
                style={{
                    // Required for text-wrapping shenanigans below.
                    width: '100%'
                }}
            >
                <div
                    className="mr-2"
                    title={setup.name}
                    style={{
                        // This degeneracy is needed to pull off
                        // text-wrapping inside a flexed div
                        // correctly. In short, we're saying that the
                        // minium width of this div is 0 (basis &
                        // min-width handles that), and then we tell
                        // it to grow to any remaining width.
                        //
                        // Furthermore we slap on all the overflow
                        // styling to ensure the text gets cut off.
                        //
                        // This shit is _extremely_ fragile and
                        // cryptic to pull off, expect things to break
                        // if you touch it.
                        flexBasis: 0,
                        flexGrow: 1,
                        whiteSpace: 'nowrap',
                        wordBreak: 'break-word',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}
                >
                    {setup.name}
                </div>

                {specs.length >= 1 && (
                    <div className="d-flex space-mx-1">
                        {specs.slice(0, 5).map((spec, index) => {
                            return (
                                <div
                                    key={index}
                                    className="small d-flex align-items-center py-1"
                                >
                                    <GameIcon
                                        className={`border-color-${CLASS_META[spec.classId].shortName} border`}
                                        size="16px"
                                        icon_name={spec.icon_name}
                                    />
                                </div>
                            )
                        })}

                        {moreThan5Healers && (
                            <div className="small d-flex align-items-center py-1">
                                <div
                                    className="border d-flex align-items-center justify-content-center small border-dark"
                                    style={{
                                        width: 16,
                                        height: 16
                                    }}
                                >
                                    {'6+'}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        )
    }

    if (props.select.selectedSetup == null) {
        return (
            <div
                className={`disabled-input form-control form-control-${props.input.bsSize} px-2 d-flex align-items-center`}
            >
                {'No setups created'}
            </div>
        )
    }

    return (
        <div
            ref={containerRef}
            className="cursor-pointer user-select-none flex-grow-1"
            style={{
                position: 'relative',
            }}
            onKeyDown={(e) => {
                if (e.key === 'Tab') {
                    setShowDropdown(false)
                }
            }}
        >

            {/* FAKE-INPUT-STARTS */}
            <div
                tabIndex={0}
                className={`form-control form-control-${props.input.bsSize} d-flex align-items-center`}
                style={{
                    padding: `0.5em 2em 0.5em 0.5em`,
                    background: `#fff url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='4' height='5' viewBox='0 0 4 5'%3e%3cpath fill='%23343a40' d='M2 0L0 2h4zm0 5L0 3h4z'/%3e%3c/svg%3e") right 0.75rem center/8px 10px no-repeat`,
                }}
                onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') {
                        e.preventDefault()
                        setShowDropdown(true)
                        setSelectedIndex(updateIndexCircular(selectedIndex, 0, props.select.setups.length - 1, 'down'))
                    }
                    if (e.key === 'ArrowUp') {
                        e.preventDefault()
                        setShowDropdown(true)
                        setSelectedIndex(updateIndexCircular(selectedIndex, 0, props.select.setups.length - 1, 'up'))
                    }
                    if (e.key === ' ') {
                        e.preventDefault()
                        if (!showDropdown) {
                            setShowDropdown(true)
                        }
                    }
                    if (e.key === 'Enter') {
                        e.preventDefault()
                        if (selectedIndex == null) {
                            return
                        }

                        props.select.onSelected(props.select.setups[selectedIndex])
                        setShowDropdown(false)
                        setSelectedIndex(null)
                    }
                    if (e.key === 'Escape') {
                        e.preventDefault()
                        setShowDropdown(false)
                        setSelectedIndex(null)
                    }
                }}
                onMouseDown={() => {
                    setShowDropdown(!showDropdown)
                    setSelectedIndex(null)
                }}
            >
                {renderSelectedSetup(expectOneById(props.select.setups, props.select.selectedSetup ?? 'never-happens'))}
            </div>
            {/* FAKE-INPUT-ENDS */}

            {showDropdown && (
                <div
                    ref={dropdownRef}
                    className="rounded bg-white mt-2 border shadow bottom-separator-border overflow-y-auto"
                    style={{
                        position: 'absolute',
                        zIndex: 1,
                        maxHeight: 300,
                        scrollbarColor: 'silver whitesmoke',
                        scrollbarWidth: 'thin',
                        whiteSpace: 'nowrap',
                        minWidth: 220
                    }}
                >
                    {props.select.setups.map((item, index) => renderSetupRow(item, selectedIndex === index))}
                </div>
            )}
        </div>
    )
}
